import React, { useState, useEffect } from "react";
import { useSnapshot } from "valtio";
import { currencyList } from "../StaticData";
import MonApi, { GET_BOOKINGS_LIST_URI, GET_SH_ENTITIES_NAME_AND_ID_URI, UPDATE_BOOKING_FIELDS_URI } from '../state/mongo-api';
import Api from "../state/Api";
import deleteicon from "../assets/images/deletei.svg";
import { bookingsStore, configStore, AgmodalStore, EntityV2Store } from "../state/CommonState";
import EntityApi from "../state/entity-api";
import BookingsApi from "../state/bookings-api";

const ChargesErp = ({ brec }) => {
  const snapBookings = useSnapshot(bookingsStore);
  const [entities, setEntities] = useState([]);
  const [rows, setRows] = useState(brec?.contract?.chargesErp || [{}]);
  const [syncState, setSyncState] = useState(brec?.contract?.chargesErpState || {});
  const [hideSynced, setHideSynced] = useState(false);
  const isFrozen = false;

  const getChargeDescription = (codeOrDesc) => {
    if (!codeOrDesc) return "";
    const charge = configStore.chargeDescriptionList?.find(
      c => c.chargeCode === codeOrDesc || c.code === codeOrDesc || c._id === codeOrDesc
    );
    return charge ? charge._id : codeOrDesc;
  };

  const getChargeCode = (codeOrDesc) => {
    if (!codeOrDesc) return "";
    const charge = configStore.chargeDescriptionList?.find(
      c => c.chargeCode === codeOrDesc || c.code === codeOrDesc || c._id === codeOrDesc
    );
    return charge ? (charge.chargeCode || charge.code || "") : codeOrDesc;
  };

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        EntityApi.getList((list) => {
          setEntities(Array.isArray(list) ? list : []);
        });
      } catch (err) {
        console.error("Error fetching entities:", err);
      }
    };
    fetchEntities();
  }, []);

  useEffect(() => {
    if (brec?._id) {
      BookingsApi.getBookingWithEntitie(brec._id, (updatedBooking) => {
        if (updatedBooking && updatedBooking.contract) {
          if (updatedBooking.contract.chargesErp) {
            setRows(JSON.parse(JSON.stringify(updatedBooking.contract.chargesErp)));
          }
          if (updatedBooking.contract.chargesErpState) {
            setSyncState(updatedBooking.contract.chargesErpState);
          }
        }
      });
    }
    if (snapBookings.current?.contract?.chargesErpState) {
      setSyncState(JSON.parse(JSON.stringify(snapBookings.current.contract.chargesErpState)));
    }
  }, [snapBookings.current?.contract?.chargesErp, snapBookings.current?.contract?.chargesErpState]);

  const getShipmentProductType = () => {
    const sType = brec?.contract?.shipmentType || brec?.shipmentType || "lcl";
    const service = String(sType).toLowerCase();
    if (service.includes("air")) return "air";
    if (service.includes("fcl")) return "fcl";
    return "lcl";
  };

  const chargeCodeOptions = [
    "",
    ...new Set(
      (configStore.getChargeCodeList
        ? configStore.getChargeCodeList(getShipmentProductType())
        : (configStore.chargeDescriptionList || [])
            .filter(e => (e[getShipmentProductType()] || "").toLowerCase() === "yes")
            .map(e => e.chargeCode)
      ).filter(Boolean)
    )
  ];
  const chargeOptions = configStore.getChargeDescriptionList(getShipmentProductType()) || [];
  const unitOptions = ['KG(s)', 'CBM(s)', 'CONTAINER(s)', 'SHIPMENT(s)', 'TONNE(s)', 'W/M'];

  const columns = [
    { key: "sno", label: "#", width: 35 },

    { key: "chargeCode", label: "Charge Code", width: 120, type: "select", options: chargeCodeOptions },
    { key: "chargeName", label: "Charge *", width: 170, type: "text", options: chargeOptions },


    { key: "term", label: "Term *", width: 90, type: "select", options: ["Prepaid", "Collect"] },

    { key: "unit", label: "Unit *", width: 80, type: "select", options: unitOptions },

    { key: "noOfUnit", label: "Units *", width: 65, type: "number" },

    // Sell
    { key: "currency", label: "Currency *", group: "Sell", width: 80, type: "select", options: currencyList },
    { key: "roe", label: "ROE *", group: "Sell", width: 60, type: "number" },
    { key: "amountPerUnit", label: "Per Unit *", group: "Sell", width: 80, type: "number" },
    { key: "amount", label: "Amount", group: "Sell", width: 80, type: "calculated" },
    { key: "localAmount", label: "Local Amt.", group: "Sell", width: 85, type: "calculated" },
    { key: "billToId", label: "Bill To *", group: "Sell", width: 140, type: "entitySelect" },
    { key: "sellNotes", label: "Bill To Narration", group: "Sell", width: 140, type: "textarea" },

    // Cost
    { key: "costCurrency", label: "Currency *", group: "Cost", width: 80, type: "select", options: currencyList },
    { key: "costRoe", label: "ROE *", group: "Cost", width: 60, type: "number" },
    { key: "costPerUnit", label: "Per Unit *", group: "Cost", width: 80, type: "number" },
    { key: "costAmount", label: "Amount", group: "Cost", width: 80, type: "calculated" },
    { key: "costLocalAmount", label: "Local Amt.", group: "Cost", width: 85, type: "calculated" },
    { key: "vendorId", label: "Vendor *", group: "Cost", width: 130, type: "entitySelect" },
    { key: "notes", label: "Cost Narration", group: "Cost", width: 140, type: "textarea" },

    { key: "actions", label: "Actions", width: 45, type: "actions" }
  ];

  const [isSyncing, setIsSyncing] = useState(false);
  const getCol = (key) => columns.find(c => c.key === key) || {};
  const commonColumns = columns.filter(c => !c.group && c.key !== "actions");
  const sellColumns = columns.filter(c => c.group === "Sell");
  const costColumns = columns.filter(c => c.group === "Cost");

  const getCellStickyStyle = (colKey, isHeader = false) => {
    if (colKey === "sno") {
      return {
        position: "sticky",
        left: 0,
        zIndex: isHeader ? 30 : 10,
        background: isHeader ? "#fafbfc" : "#fff",
        borderRight: "1px solid #e1e8f5"
      };
    }
    if (colKey === "chargeCode") {
      return {
        position: "sticky",
        left: "35px",
        zIndex: isHeader ? 30 : 10,
        background: isHeader ? "#fafbfc" : "#fff",
        borderRight: "1px solid #e1e8f5"
      };
    }
    if (colKey === "chargeName") {
      return {
        position: "sticky",
        left: "155px",
        zIndex: isHeader ? 30 : 10,
        background: isHeader ? "#fafbfc" : "#fff",
        borderRight: "1px solid #e1e8f5"
      };
    }
    if (colKey === "actions") {
      return {
        position: "sticky",
        right: 0,
        zIndex: isHeader ? 30 : 10,
        background: isHeader ? "#fafbfc" : "#fff",
        borderLeft: "1px solid #e1e8f5"
      };
    }
    return {};
  };

  const cargoList = brec?.contract?.cargoList || [];

  const handleCellChange = (rowIndex, key, value) => {
    const updatedRows = [...rows];
    let newRow = { ...updatedRows[rowIndex], [key]: value };
    if (key === "currency") {
      newRow.roe = value === "INR" ? 1 : (configStore.getExchangeRate(value) || "");
    } else if (key === "costCurrency") {
      newRow.costRoe = value === "INR" ? 1 : (configStore.getExchangeRate(value) || "");
    } else if (key === "chargeCode") {
      const chargeObj = configStore.chargeDescriptionList?.find(c => c.chargeCode === value || c.code === value);
      newRow.chargeCode = value;
      if (chargeObj && chargeObj._id) {
        newRow.chargeName = chargeObj._id;
      }
    } else if (key === "chargeName") {
      newRow.chargeName = value;
      const chargeObj = configStore.chargeDescriptionList?.find(c => c._id === value);
      if (chargeObj && (chargeObj.chargeCode || chargeObj.code)) {
        newRow.chargeCode = chargeObj.chargeCode || chargeObj.code;
      }
    } else if (key === "billToId") {

      const ent = Array.isArray(entities) ? entities.find(e => (e.entityId === value || e._id === value)) : null;
      newRow.billToCustomer = ent ? ent.entityName : "";
    } else if (key === "vendorId") {
      const ent = Array.isArray(entities) ? entities.find(e => (e.entityId === value || e._id === value)) : null;
      newRow.vendor = ent ? ent.entityName : "";
    } else if (key === "unit") {
      const valLower = String(value).toLowerCase();
      if (valLower === "container" || valLower === "shipment") {
        newRow.noOfUnit = 1;
      }
    }
    updatedRows[rowIndex] = newRow;
    setRows(updatedRows);
  };

  const addRow = (e) => {
    e.preventDefault();
    setRows([...rows, {}]);
  };

  const isRowValid = (row) => {
    const isJobRow = !!row.job || String(row.job).toLowerCase() === "true";

    const commonAndCostKeys = [
      "chargeCode",
      "chargeName",
      "term",
      "unit",
      "noOfUnit",
      "costCurrency",
      "costPerUnit",
      "vendorId",
    ];

    const sellKeys = [
      "currency",
      "amountPerUnit",
      "billToId",
    ];

    const requiredKeys = isJobRow ? commonAndCostKeys : [...commonAndCostKeys, ...sellKeys];

    const mainFieldsValid = requiredKeys.every(key => {
      let val = row[key];
      if (key === "chargeCode" && (!val || String(val).trim() === "") && row.chargeName) {
        val = getChargeCode(row.chargeName);
      }
      return val !== undefined && val !== null && String(val).trim() !== "";
    });

    if (!mainFieldsValid) return false;

    const costRoeValid = row.costCurrency === "INR" || (row.costRoe !== undefined && row.costRoe !== null && String(row.costRoe).trim() !== "");

    if (isJobRow) {
      return costRoeValid;
    }

    const roeValid = row.currency === "INR" || (row.roe !== undefined && row.roe !== null && String(row.roe).trim() !== "");

    return roeValid && costRoeValid;
  };

  const handleSave = async (e) => {
    e.preventDefault()
    if (!brec || !brec._id) {
      configStore.setModalMessage("No booking record ID found");
      return;
    }
    if (rows.length === 0 || (rows.length === 1 && Object.keys(rows[0]).length === 0)) {
      configStore.setModalMessage("No records in the table found to save.");
      return;
    }
    for (let i = 0; i < rows.length; i++) {
      if (!isRowValid(rows[i])) {
        const isJobRow = !!rows[i].job || String(rows[i].job).toLowerCase() === "true";
        if (isJobRow) {
          configStore.setModalMessage(`Please fill all required cost & charge fields for row ${i + 1}.`);
        } else {
          configStore.setModalMessage(`Please fill all fields for row ${i + 1}. All fields in a row are mandatory.`);
        }
        return;
      }
    }
    try {
      const calculatedRows = rows.map(r => {
        const isJobRow = !!r.job || String(r.job).toLowerCase() === "true";
        const units = parseFloat(r.noOfUnit) || 0;
        const amountPerUnit = parseFloat(r.amountPerUnit) || 0;
        const roe = r.currency === "INR" ? 1 : (parseFloat(r.roe) || 0);
        const costPerUnit = parseFloat(r.costPerUnit) || 0;
        const costRoe = r.costCurrency === "INR" ? 1 : (parseFloat(r.costRoe) || 0);
        const chargeCodeVal = r.chargeCode || getChargeCode(r.chargeName);
        return {
          ...r,
          chargeCode: chargeCodeVal,
          ...(isJobRow ? {} : { roe }),
          costRoe,
          amount: parseFloat((units * amountPerUnit).toFixed(2)),
          localAmount: parseFloat((units * amountPerUnit * roe).toFixed(2)),
          costAmount: parseFloat((units * costPerUnit).toFixed(2)),
          costLocalAmount: parseFloat((units * costPerUnit * costRoe).toFixed(2))
        };
      });
      Api.updateProRateBookings({ id: brec._id, chargesErp: calculatedRows }, (data) => {
        if (brec.contract) {
          brec.contract.chargesErp = calculatedRows;
        }
        setRows(calculatedRows);
        configStore.setModalMessage("Charges saved!");
      });
    } catch (err) {
      console.error(err);
      configStore.setModalMessage("Error saving charges.");
    }
  };

  const handleSync = (e) => {
    e.preventDefault()
    if (!brec || !brec._id) {
      configStore.setModalMessage("No booking record ID found");
      return;
    }
    
    setIsSyncing(true);
    try {
      Api.chargesErpSync(brec._id, (data) => {
        setIsSyncing(false);
        if (data.msg === "Request intitated") {
            const chargesErpState = data?.chargesErpState;
            const newSyncState = {
              lastAt: chargesErpState?.chargesSyncAt,
              lastBy: chargesErpState?.lastChargesSync,
              lastStatus: chargesErpState?.erpSyncStatus,
              remarks: chargesErpState?.remarks
            };
            setSyncState(newSyncState);
            if (bookingsStore.current && bookingsStore.current.contract) {
              bookingsStore.current.contract.chargesErpState = newSyncState;
            }
        } else {
          configStore.setModalMessage(data.msg);
        }
      }, "booking");
    } catch (err) {
      setIsSyncing(false);
      console.error(err);
      configStore.setModalMessage("Error initiating sync.");
    }
  };

  const renderCell = (column, row, rowIndex) => {
    const value = row[column.key] || "";
    const isRowFrozen = isFrozen || row.job === true ;
    const inputStyle = {
      width: "100%",
      border: "1px solid #dcdde1",
      borderRadius: "4px",
      padding: "4px 6px",
      fontSize: "11px",
      outline: "none",
      boxSizing: "border-box",
      fontFamily: "inherit",
      backgroundColor: "#fff",
      transition: "border-color 0.2s, box-shadow 0.2s",
      minHeight: "26px"
    };

    switch (column.type) {
      case "checkbox":
        return (
          <input
            type="checkbox"
            checked={!!row[column.key]}
            disabled={isRowFrozen}
            onChange={e => handleCellChange(rowIndex, column.key, e.target.checked)}
            style={{ cursor: isRowFrozen ? "not-allowed" : "pointer", transform: "scale(1.1)" }}
          />
        );

      case "textarea":
        return (
          <textarea
            rows={1}
            value={value}
            disabled={isRowFrozen}
            onChange={e => handleCellChange(rowIndex, column.key, e.target.value)}
            style={{
              ...inputStyle,
              resize: "none",
              minHeight: "32px",
              verticalAlign: "middle",
              ...(isRowFrozen ? { backgroundColor: "#f5f6fa", color: "#7f8c8d", cursor: "not-allowed" } : {})
            }}
          />
        );

      case "button":
        return <button className="button is-small is-light" disabled={isRowFrozen}>⋮</button>;

      case "actions":
        if (isRowFrozen) {
          return <span style={{ color: "#bdc3c7", fontSize: "11px", fontWeight: "600" }}>Locked</span>;
        }
        return (
          <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
            <button
              onClick={() => {
                const updatedRows = rows.filter((_, idx) => idx !== rowIndex);
                setRows(updatedRows.length > 0 ? updatedRows : [{}]);
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px 6px",
                display: "flex",
                alignItems: "center"
              }}
              title="Delete Row"
            >
              <img src={deleteicon} style={{ width: "16px", height: "16px" }} alt="Delete"></img>
            </button>
          </div>
        );

      case "select":
        let defaultOptionText = "Select Option";
        if (column.key === "term") {
          defaultOptionText = "Select Term";
        } else if (column.key === "currency" || column.key === "costCurrency") {
          defaultOptionText = "Select Currency";
        } else if (column.key === "chargeCode") {
          defaultOptionText = "Select Code";
        } else if (column.key === "unit") {
          defaultOptionText = "Select Unit";
        }

        const selectValue = column.key === "chargeCode" ? (value || getChargeCode(row.chargeName)) : value;

        let optionsToRender = column.options;
        if (column.key === "chargeCode" && Array.isArray(optionsToRender)) {
          const otherSelectedCodes = new Set(
            rows
              .filter((_, idx) => idx !== rowIndex)
              .map(r => r.chargeCode || getChargeCode(r.chargeName))
              .filter(Boolean)
          );
          optionsToRender = optionsToRender.filter(opt => !opt || opt === selectValue || !otherSelectedCodes.has(opt));
        }

        return (
          <select
            value={selectValue}
            disabled={isRowFrozen}
            onChange={e => handleCellChange(rowIndex, column.key, e.target.value)}
            style={{
              ...inputStyle,
              cursor: isRowFrozen ? "not-allowed" : "pointer",
              appearance: "none",
              backgroundImage: "url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%237f8c8d%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 8px top 50%",
              backgroundSize: "8px auto",
              paddingRight: "24px",
              ...(isRowFrozen ? { backgroundColor: "#f5f6fa", color: "#7f8c8d" } : {})
            }}
          >
             <option value="" disabled hidden>{defaultOptionText}</option>
             {selectValue && optionsToRender && !optionsToRender.includes(selectValue) && (
               <option key={selectValue} value={selectValue}>
                 {selectValue}
               </option>
             )}
             {optionsToRender?.filter(opt => opt !== "").map(opt => (
               <option key={opt} value={opt}>
                 {opt}
               </option>
             ))}
          </select>
        );

      case "entitySelect":
        return (
          <select
            value={value}
            disabled={isRowFrozen}
            onChange={e => handleCellChange(rowIndex, column.key, e.target.value)}
            style={{
              ...inputStyle,
              cursor: isRowFrozen ? "not-allowed" : "pointer",
              appearance: "none",
              backgroundImage: "url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%237f8c8d%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 8px top 50%",
              backgroundSize: "8px auto",
              paddingRight: "24px",
              ...(isRowFrozen ? { backgroundColor: "#f5f6fa", color: "#7f8c8d" } : {})
            }}
          >
            <option value="" disabled hidden>
              {column.key === "billToId" ? "Select Customer" : "Select Vendor"}
            </option>
            {value && Array.isArray(entities) && !entities.some(ent => (ent.entityId === value || ent._id === value)) && (
               <option key={value} value={value}>
                 {column.key === "billToId" ? (row.billToCustomer || value) : (row.vendor || value)}
               </option>
             )}
            {Array.isArray(entities) && entities.map(ent => {
              const idVal = ent.entityId || ent._id;
              return (
                <option key={idVal} value={idVal}>
                  {ent.entityName}
                </option>
              );
            })}
          </select>
        );

      case "calculated":
        let calcVal = "";
        const unitsVal = parseFloat(row.noOfUnit) || 0;
        if (column.key === "amount") {
          calcVal = (unitsVal * (parseFloat(row.amountPerUnit) || 0)).toFixed(2);
        } else if (column.key === "localAmount") {
          const roeVal = row.currency === "INR" ? 1 : (parseFloat(row.roe) || 0);
          calcVal = (unitsVal * (parseFloat(row.amountPerUnit) || 0) * roeVal).toFixed(2);
        } else if (column.key === "costAmount") {
          calcVal = (unitsVal * (parseFloat(row.costPerUnit) || 0)).toFixed(2);
        } else if (column.key === "costLocalAmount") {
          const costRoeVal = row.costCurrency === "INR" ? 1 : (parseFloat(row.costRoe) || 0);
          calcVal = (unitsVal * (parseFloat(row.costPerUnit) || 0) * costRoeVal).toFixed(2);
        }
        return (
          <input
            type="text"
            readOnly
            value={calcVal}
            style={{
              ...inputStyle,
              backgroundColor: "#f5f6fa",
              color: "#7f8c8d",
              fontWeight: "600",
              cursor: "not-allowed"
            }}
          />
        );

      case "number":
        const isRoeLocked = (column.key === "roe" && row.currency === "INR") || 
                            (column.key === "costRoe" && row.costCurrency === "INR");
        const isUnitsDisabled = column.key === "noOfUnit" && 
                                (String(row.unit).toLowerCase() === "container" || String(row.unit).toLowerCase() === "shipment");
        return (
          <input
            type="number"
            step="any"
            value={isUnitsDisabled ? 1 : (isRoeLocked ? 1 : value)}
            disabled={isRowFrozen || isRoeLocked || isUnitsDisabled}
            onChange={e => handleCellChange(rowIndex, column.key, e.target.value)}
            style={{
              ...inputStyle,
              ...((isRowFrozen || isRoeLocked || isUnitsDisabled) ? { backgroundColor: "#f5f6fa", color: "#7f8c8d", cursor: "not-allowed" } : {})
            }}
          />
        );

      default:
        let inputOptions = column.options;
        if (column.key === "chargeName" && Array.isArray(inputOptions)) {
          const otherSelectedCodes = new Set(
            rows
              .filter((_, idx) => idx !== rowIndex)
              .map(r => r.chargeCode || getChargeCode(r.chargeName))
              .filter(Boolean)
          );
          inputOptions = inputOptions.filter(opt => {
            if (!opt || opt === value) return true;
            const code = getChargeCode(opt);
            return !code || !otherSelectedCodes.has(code);
          });
        }
        const hasOptions = Array.isArray(inputOptions) && inputOptions.length > 0;
        const listId = hasOptions ? `datalist_${column.key}_${rowIndex}` : undefined;
        return (
          <>
            <input
              type="text"
              value={value}
              list={listId}
              placeholder={column.key === "chargeName" ? "Enter or select charge" : ""}
              disabled={isRowFrozen}
              onChange={e => handleCellChange(rowIndex, column.key, e.target.value)}
              style={{
                ...inputStyle,
                ...(isRowFrozen ? { backgroundColor: "#f5f6fa", color: "#7f8c8d", cursor: "not-allowed" } : {})
              }}
            />
            {hasOptions && (
              <datalist id={listId}>
                {inputOptions.map((opt, i) => (
                  <option key={i} value={opt} />
                ))}
              </datalist>
            )}
          </>
        );

    }
  };

  const isSaved = JSON.stringify(rows) === JSON.stringify(brec?.contract?.chargesErp || [{}]);
  const hasRecords = rows.length > 0 && !(rows.length === 1 && Object.keys(rows[0]).length === 0) && rows.every(row => isRowValid(row));
  const hasNonJobRecord = rows.some(row => !row.job && row.job !== true && String(row.job).toLowerCase() !== "true" && Object.keys(row).length > 0 && isRowValid(row));
  const lastAt = syncState?.lastAt;
  const lastBy = syncState?.lastBy;
  const lastStatus = syncState?.lastStatus;
  const remarks = syncState?.remarks;


  const [lastChecked, setLastChecked] = useState("Just now");

  useEffect(() => {
    let timer;
    if (lastStatus === "PROCESSING") {
      setLastChecked("Just now");
      timer = setInterval(async () => {
        try {
          BookingsApi.getBookingWithEntitie(brec._id, (updatedBooking) => {
            if (updatedBooking) {
              console.log("Polled booking:", updatedBooking);
              console.log("Polled chargesErpState:", updatedBooking?.contract?.chargesErpState);
              const newSyncState = updatedBooking.contract?.chargesErpState || {};
              setSyncState(newSyncState);
              if (bookingsStore.current && updatedBooking.contract) {
                bookingsStore.current.contract.chargesErp = updatedBooking.contract.chargesErp;
                if (!bookingsStore.current.contract.chargesErpState) {
                  bookingsStore.current.contract.chargesErpState = {};
                }
                bookingsStore.current.contract.chargesErpState.lastAt = newSyncState.lastAt;
                bookingsStore.current.contract.chargesErpState.lastBy = newSyncState.lastBy;
                bookingsStore.current.contract.chargesErpState.lastStatus = newSyncState.lastStatus;
                bookingsStore.current.contract.chargesErpState.remarks = newSyncState.remarks;
                if (updatedBooking.contract.chargesErp) {
                  setRows(updatedBooking.contract.chargesErp);
                }
              }
            }
            setLastChecked(new Date().toLocaleTimeString());
          });
        } catch (e) {
          console.error("Error polling booking status", e);
        }
      }, 10000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [lastStatus, brec._id]);

  if (lastStatus === "PROCESSING") {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px",
        textAlign: "center",
        background: "#f2f6ff",
        borderRadius: "12px",
        border: "1px dashed #2B6ED4",
        margin: "20px 0",
        boxShadow: "0 4px 15px rgba(44, 53, 138, 0.1)"
      }}>
        <style>{`
          @keyframes scan {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(250%); }
          }
        `}</style>
        <div style={{ fontSize: "40px", marginBottom: "15px" }}>
          🚢 ✈️ 🌐 📦
        </div>
        <h3 style={{ fontSize: "18px", fontWeight: "bold", color: "#2C358A", marginBottom: "10px" }}>
          Sync in Progress!
        </h3>
        <p style={{ fontSize: "14px", color: "#3E56C4", marginBottom: "20px", maxWidth: "450px" }}>
          ERP sync is processing in the background. You can wait here or continue with other work and check back later!
        </p>
        
        {/* Indeterminate Progress Bar */}
        <div style={{
          width: "80%",
          maxWidth: "350px",
          height: "8px",
          backgroundColor: "#d6e2ff",
          borderRadius: "4px",
          overflow: "hidden",
          position: "relative",
          marginBottom: "15px"
        }}>
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            width: "40%",
            background: "linear-gradient(90deg, transparent, #2B6ED4, transparent)",
            borderRadius: "4px",
            animation: "scan 1.5s infinite ease-in-out"
          }} />
        </div>

        <div style={{
          fontSize: "13px",
          color: "#7f8c8d",
          fontWeight: "500",
          backgroundColor: "#fff",
          padding: "8px 16px",
          borderRadius: "20px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
        }}>
          Last checked: <span style={{ color: "#1B2479", fontWeight: "bold" }}>{lastChecked}</span>
        </div>
      </div>
    );
  }

  const calculateSummary = () => {
    let totalSell = 0;
    let totalCost = 0;

    rows.forEach(r => {
      const units = parseFloat(r.noOfUnit) || 0;
      const amountPerUnit = parseFloat(r.amountPerUnit) || 0;
      const roe = parseFloat(r.roe) || 0;
      const costPerUnit = parseFloat(r.costPerUnit) || 0;
      const costRoe = parseFloat(r.costRoe) || 0;

      totalSell += units * amountPerUnit * roe;
      totalCost += units * costPerUnit * costRoe;
    });

    const profit = totalSell - totalCost;
    const profitPercent = totalSell !== 0 ? (profit / totalSell) * 100 : 0;

    return {
      totalSell: totalSell.toFixed(2),
      totalCost: totalCost.toFixed(2),
      profit: profit.toFixed(2),
      profitPercent: profitPercent.toFixed(2)
    };
  };

  const summary = calculateSummary();

  return (
    <div>
      {cargoList.length > 0 && (
        <div style={{
          marginBottom: "25px",
          background: "#fff",
          borderRadius: "8px",
          border: "1px solid #e1e8f5",
          padding: "16px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.02)"
        }}>
          <h4 style={{ fontWeight: "700", marginBottom: "12px", fontSize: "1rem", color: "#2C358A", display: "flex", alignItems: "center", gap: "6px" }}>
            📦 Cargo Details
          </h4>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                borderCollapse: "collapse",
                width: "100%",
                fontSize: "12px"
              }}
            >
              <thead>
                <tr style={{ background: "#f0f4fd", color: "#2C358A" }}>
                  <th style={{ border: "1px solid #e1e8f5", padding: "10px", fontWeight: "600", textAlign: "left" }}>Package Type</th>
                  <th style={{ border: "1px solid #e1e8f5", padding: "10px", fontWeight: "600", textAlign: "left" }}>Num Packages</th>
                  <th style={{ border: "1px solid #e1e8f5", padding: "10px", fontWeight: "600", textAlign: "left" }}>Wt / Package</th>
                  <th style={{ border: "1px solid #e1e8f5", padding: "10px", fontWeight: "600", textAlign: "left" }}>Length</th>
                  <th style={{ border: "1px solid #e1e8f5", padding: "10px", fontWeight: "600", textAlign: "left" }}>Width</th>
                  <th style={{ border: "1px solid #e1e8f5", padding: "10px", fontWeight: "600", textAlign: "left" }}>Height</th>
                  <th style={{ border: "1px solid #e1e8f5", padding: "10px", fontWeight: "600", textAlign: "left" }}>Vol / Package</th>
                  <th style={{ border: "1px solid #e1e8f5", padding: "10px", fontWeight: "600", textAlign: "left" }}>Total Weight</th>
                  <th style={{ border: "1px solid #e1e8f5", padding: "10px", fontWeight: "600", textAlign: "left" }}>Total Volume</th>
                  <th style={{ border: "1px solid #e1e8f5", padding: "10px", fontWeight: "600", textAlign: "left" }}>Chargeable Weight</th>
                </tr>
              </thead>
              <tbody>
                {cargoList.map((cargo, idx) => (
                  <tr key={cargo.id ?? idx} style={{ transition: "background 0.2s" }} onMouseEnter={e => e.currentTarget.style.background = "#fafbfc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ border: "1px solid #e1e8f5", padding: "10px", color: "#4a5568" }}>{cargo.packageType}</td>
                    <td style={{ border: "1px solid #e1e8f5", padding: "10px", color: "#4a5568" }}>{cargo.numPackages}</td>
                    <td style={{ border: "1px solid #e1e8f5", padding: "10px", color: "#4a5568" }}>{cargo.wtPerPackage}</td>
                    <td style={{ border: "1px solid #e1e8f5", padding: "10px", color: "#4a5568" }}>{cargo.length}</td>
                    <td style={{ border: "1px solid #e1e8f5", padding: "10px", color: "#4a5568" }}>{cargo.width}</td>
                    <td style={{ border: "1px solid #e1e8f5", padding: "10px", color: "#4a5568" }}>{cargo.height}</td>
                    <td style={{ border: "1px solid #e1e8f5", padding: "10px", color: "#4a5568" }}>{cargo.volPerPackage}</td>
                    <td style={{ border: "1px solid #e1e8f5", padding: "10px", color: "#4a5568" }}>{cargo.totalWeight}</td>
                    <td style={{ border: "1px solid #e1e8f5", padding: "10px", color: "#4a5568" }}>{cargo.totalVolume}</td>
                    <td style={{ border: "1px solid #e1e8f5", padding: "10px", color: "#4a5568" }}>{cargo.chargeableWeight}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div style={{
        background: "#fff",
        borderRadius: "8px",
        border: "1px solid #e1e8f5",
        padding: "12px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.02)"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <h4 style={{ fontWeight: "700", margin: 0, fontSize: "0.95rem", color: "#2C358A" }}>
            💼 Charges for ERP
          </h4>
          <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "#4a5568", cursor: "pointer", fontWeight: "600" }}>
            <input
              type="checkbox"
              checked={hideSynced}
              onChange={e => setHideSynced(e.target.checked)}
              style={{ cursor: "pointer" }}
            />
            Hide Job Charges
          </label>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              borderCollapse: "collapse",
              width: "max-content",
              minWidth: "100%",
              fontSize: "11px"
            }}
          >
            <thead>
              <tr>
                <th style={{ ...getCellStickyStyle("sno", true), border: "1px solid #e1e8f5" }} />
                <th style={{ ...getCellStickyStyle("chargeCode", true), border: "1px solid #e1e8f5" }} />
                <th style={{ ...getCellStickyStyle("chargeName", true), border: "1px solid #e1e8f5" }} />
                <th colSpan={3} style={{ border: "1px solid #e1e8f5", padding: "4px", background: "#fafbfc" }} />

                <th
                  colSpan={sellColumns.length}
                  style={{
                    border: "1px solid #e1e8f5",
                    padding: "6px",
                    background: "#e8f8f0",
                    color: "#1e8449",
                    fontWeight: "700",
                    textAlign: "center"
                  }}
                >
                  Sell
                </th>

                <th
                  colSpan={costColumns.length}
                  style={{
                    border: "1px solid #e1e8f5",
                    padding: "6px",
                    background: "#fdf2e9",
                    color: "#d35400",
                    fontWeight: "700",
                    textAlign: "center"
                  }}
                >
                  Cost
                </th>

                <th style={{ ...getCellStickyStyle("actions", true), border: "1px solid #e1e8f5", padding: "4px" }} />
              </tr>

              <tr>
                {columns.map(col => (
                  <th
                    key={col.key}
                    style={{
                      border: "1px solid #e1e8f5",
                      padding: "6px",
                      minWidth: col.width,
                      background: col.group === "Sell" ? "#f4fbf8" : col.group === "Cost" ? "#fef9f5" : "#fafbfc",
                      color: col.group === "Sell" ? "#196f3d" : col.group === "Cost" ? "#ba4a00" : "#4a5568",
                      fontWeight: "600",
                      textAlign: "left",
                      ...getCellStickyStyle(col.key, true)
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((row, rowIndex) => {
                const isRowFrozen = !!row.job || String(row.job).toLowerCase() === "true";
                return (
                  <tr
                    key={rowIndex}
                    style={{
                      transition: "background 0.2s",
                      ...(hideSynced && isRowFrozen ? { display: "none" } : {})
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#fcfdfe"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                  {columns.map(col => (
                    <td
                      key={col.key}
                      style={{
                        border: "1px solid #e1e8f5",
                        padding: "4px",
                        minWidth: col.width,
                        verticalAlign: "middle",
                        backgroundColor: col.group === "Sell" ? "#f4fbf7" : col.group === "Cost" ? "#fdf6f0" : "transparent",
                        ...getCellStickyStyle(col.key, false)
                      }}
                    >
                      {col.key === "sno"
                        ? rowIndex + 1
                        : renderCell(col, row, rowIndex)}
                    </td>
                  ))}
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>

      {(() => {
        const isLoss = parseFloat(summary.profit) < 0;
        const profitColor = isLoss ? "#e74c3c" : "#2ecc71";
        const displayProfit = isLoss ? Math.abs(parseFloat(summary.profit)).toFixed(2) : summary.profit;
        const displayProfitPercent = isLoss ? Math.abs(parseFloat(summary.profitPercent)).toFixed(2) : summary.profitPercent;
        
        return (
          <div style={{
            marginTop: "20px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
            alignItems: "start"
          }}>
            {/* Left Side: Sync Status & Note */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {(lastAt || lastBy || lastStatus) && (
                <div style={{
                  background: "#fafbfc",
                  border: "1px solid #e1e8f5",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  fontSize: "12px",
                  color: "#666",
                  lineHeight: "20px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.01)"
                }}>
                  <h5 style={{ fontWeight: "700", color: "#2C358A", marginBottom: "6px", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Sync Information
                  </h5>
                  {lastAt && (
                    <div>
                      <strong>Synced At:</strong> {new Date(lastAt).toLocaleString()}
                    </div>
                  )}
                  {lastBy && (
                    <div>
                      <strong>Synced By:</strong> {lastBy}
                    </div>
                  )}
                  {lastStatus && (
                    <div style={{ display: "flex", alignItems: "center", marginTop: "4px" }}>
                      <strong>Last Sync Status:</strong>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          fontSize: "10px",
                          fontWeight: "800",
                          color:
                            lastStatus.toUpperCase() === "SUCCESS"
                              ? "#2ecc71" // Green
                              : lastStatus.toUpperCase() === "FAILED"
                              ? "#e74c3c" // Red
                              : lastStatus.toUpperCase() === "PROCESSING"
                              ? "#f39c12" // Orange
                              : "#7f8c8d", // Gray
                          marginLeft: "6px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          background:
                            lastStatus.toUpperCase() === "SUCCESS"
                              ? "#e8f8f0"
                              : lastStatus.toUpperCase() === "FAILED"
                              ? "#fdedec"
                              : lastStatus.toUpperCase() === "PROCESSING"
                              ? "#fef5e7"
                              : "#f2f4f4",
                          padding: "2px 8px",
                          borderRadius: "12px"
                        }}
                      >
                        <span
                          style={{
                            height: "6px",
                            width: "6px",
                            borderRadius: "50%",
                            backgroundColor: "currentColor",
                            display: "inline-block",
                            marginRight: "4px"
                          }}
                        />
                        {lastStatus}
                      </span>
                    </div>
                  )}
                  {lastStatus && lastStatus.toUpperCase() === "FAILED" && remarks && (
                    <div style={{
                      marginTop: "8px",
                      padding: "8px 12px",
                      background: "#fdedec",
                      border: "1px solid #fadbd8",
                      borderRadius: "6px",
                      color: "#c0392b",
                      fontSize: "11px",
                      lineHeight: "15px",
                      wordBreak: "break-word"
                    }}>
                      <strong>Reason:</strong> {remarks}
                    </div>
                  )}
                  {lastStatus.toUpperCase() === "PROCESSING" && (
                    <div style={{ fontSize: "11px", color: "#e67e22", marginTop: "6px", fontWeight: "600" }}>
                      Sync running in background. You can close this booking and check back shortly! ☕✨
                    </div>
                  )}
                </div>
              )}
              <div style={{ fontSize: "11px", color: "#7f8c8d", fontWeight: "500", lineHeight: "15px" }}>
                * Note: Charge, Unit, Bill To, and Vendor must match NewageNXT listed values exactly. Using respective Codes is recommended.
              </div>
            </div>

            {/* Right Side: Summary Box & Action Buttons */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "16px" }}>
              <div style={{
                background: "#f8fafd",
                border: "1px solid #e1e8f5",
                borderRadius: "8px",
                padding: "16px",
                width: "100%",
                maxWidth: "280px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.01)"
              }}>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "100px 40px 1fr",
                  rowGap: "8px",
                  fontSize: "12px",
                  color: "#4a5568",
                  fontWeight: "500"
                }}>
                  <div>Total Sell</div>
                  <div>INR</div>
                  <div style={{ textAlign: "right", fontWeight: "700", color: "#2C358A" }}>{summary.totalSell}</div>

                  <div>Total Cost</div>
                  <div>INR</div>
                  <div style={{ textAlign: "right", fontWeight: "700", color: "#1B2479" }}>{summary.totalCost}</div>

                  <div style={{ borderTop: "1px solid #e1e8f5", paddingTop: "8px", fontWeight: "600" }}>{isLoss ? "Loss" : "Profit"}</div>
                  <div style={{ borderTop: "1px solid #e1e8f5", paddingTop: "8px", fontWeight: "600" }}>INR</div>
                  <div style={{ borderTop: "1px solid #e1e8f5", paddingTop: "8px", textAlign: "right", fontWeight: "700", color: profitColor }}>{displayProfit}</div>

                  <div style={{ fontWeight: "600" }}>{isLoss ? "Loss %" : "Profit %"}</div>
                  <div style={{ fontWeight: "600" }}>%</div>
                  <div style={{ textAlign: "right", fontWeight: "700", color: profitColor }}>{displayProfitPercent} %</div>
                </div>
              </div>

              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <button
                  onClick={(e)=>addRow(e)}
                  disabled={isFrozen}
                  className="button is-small"
                  style={{
                    height: "32px",
                    padding: "0 16px",
                    cursor: isFrozen ? "not-allowed" : "pointer",
                    border: isFrozen ? "1px solid #bdc3c7" : "1px solid #2B6ED4",
                    background: isFrozen ? "#f5f6fa" : "#f0f4fd",
                    color: isFrozen ? "#bdc3c7" : "#2C358A",
                    fontWeight: "600",
                    borderRadius: "6px",
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    transition: "all 0.2s"
                  }}
                >
                  <span style={{ fontSize: "14px" }}>+</span> Add Row
                </button>
                <button
                  onClick={(e)=>handleSave(e)}
                  disabled={isFrozen || lastStatus === "PROCESSING"}
                  className="button is-small"
                  style={{
                    height: "32px",
                    padding: "0 16px",
                    cursor: (isFrozen || lastStatus === "PROCESSING") ? "not-allowed" : "pointer",
                    border: "none",
                    background: (isFrozen || lastStatus === "PROCESSING") ? "#bdc3c7" : "#2ecc71",
                    color: "#fff",
                    fontWeight: "600",
                    borderRadius: "6px",
                    fontSize: "12px",
                    boxShadow: (isFrozen || lastStatus === "PROCESSING") ? "none" : "0 2px 4px rgba(46, 204, 113, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    transition: "all 0.2s"
                  }}
                >
                  Save Charges
                </button>
                <button
                  onClick={(e)=>handleSync(e)}
                  disabled={isFrozen || !isSaved || !hasRecords || !hasNonJobRecord || lastStatus === "PROCESSING" || isSyncing}
                  className="button is-small"
                  style={{
                    height: "32px",
                    padding: "0 12px",
                    cursor: (isFrozen || !isSaved || !hasRecords || !hasNonJobRecord || lastStatus === "PROCESSING" || isSyncing) ? "not-allowed" : "pointer",
                    border: "none",
                    background: (isFrozen || !isSaved || !hasRecords || !hasNonJobRecord || lastStatus === "PROCESSING" || isSyncing) ? "#bdc3c7" : "#3E56C4",
                    color: "#fff",
                    borderRadius: "6px",
                    boxShadow: (isFrozen || !isSaved || !hasRecords || !hasNonJobRecord || lastStatus === "PROCESSING" || isSyncing) ? "none" : "0 2px 4px rgba(62, 86, 196, 0.2)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s"
                  }}
                  title={!hasNonJobRecord ? "No booking charges to sync (job charges are excluded)" : "Sync with ERP"}
                >
                  <span className="icon" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="fas fa-sync"></i>
                  </span>
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ChargesErp;